import { CircularProgress, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, IconButton, InputBase, Link, makeStyles, Paper, Theme, Typography } from "@material-ui/core";
import blue from "@material-ui/core/colors/blue";
import { alpha } from "@material-ui/core/styles";
import AssistantIcon from '@material-ui/icons/AssistantOutlined';
import AttachFileIcon from '@material-ui/icons/AttachFile';
import CloseIcon from '@material-ui/icons/Close';
import SendChatMessageIcon from '@material-ui/icons/Telegram';
import { useCallback, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { useSendAssistantMessageMutation } from "../../graphql/generated/graphql";
import useInput from "../../shared/Hooks/useInput";
import useAssistantRealtime, { ChatMessage } from "./useAssistantRealtime";

const useAssistantStyles = makeStyles((theme: Theme) =>
{
  return ({
    dialogTitle: {
      margin: 0,
      padding: theme.spacing(2),
    },
    closeButton: {
      position: 'absolute',
      right: theme.spacing(1),
      top: theme.spacing(1),
    },
    dialogContent: {
      padding: theme.spacing(2),
      minHeight: 'calc(100vh - 250px)',
    },
    chatMessageIcon: {
      fontSize: 20,
      marginBottom: -5,
      marginRight: 4,
      color: blue[600],
    },
    dialogActions: {
      padding: theme.spacing(2),
    },
  });
});

const useMessageStyles = makeStyles((theme: Theme) =>
{
  const isLight = theme.palette.type === 'light';

  return ({
    messagesContainer: {
      display: 'flex',
      flexDirection: 'column',
      gap: theme.spacing(1),
    },
    messageRow: {
      display: 'flex',
    },
    messageRowUser: {
      justifyContent: 'flex-end',
    },
    messageRowAssistant: {
      justifyContent: 'flex-start',
    },
    messageBubble: {
      maxWidth: '80%',
      padding: theme.spacing(1, 1.5),
      borderRadius: 14,
      whiteSpace: 'pre-wrap',
      wordBreak: 'break-word',
    },
    messageBubbleUser: {
      backgroundColor: isLight ? alpha(blue[500], 0.12) : alpha(blue[300], 0.2),
    },
    messageBubbleAssistant: {
      backgroundColor: isLight ? alpha(theme.palette.common.black, 0.06) : alpha(theme.palette.common.white, 0.12),
    },
    markdownContent: {
      lineHeight: '18px',
      whiteSpace: 'normal',
      '& *': {
        lineHeight: 'inherit',
        whiteSpace: 'inherit',
      },
      '& p': {
        margin: '4px 0',
      },
      '& p:empty': {
        display: 'none',
      },
      '& p + p': {
        marginTop: 6,
      },
      '& ul, & ol': {
        margin: '4px 0',
        padding: 0,
        paddingLeft: theme.spacing(2),
      },
      '& li': {
        margin: '2px 0',
      },
      '& pre': {
        margin: '6px 0',
        padding: theme.spacing(1),
        borderRadius: 8,
        overflowX: 'auto',
        backgroundColor: isLight ? alpha(theme.palette.common.black, 0.08) : alpha(theme.palette.common.white, 0.14),
      },
      '& code': {
        fontFamily: 'monospace',
        fontSize: '0.85em',
        backgroundColor: isLight ? alpha(theme.palette.common.black, 0.08) : alpha(theme.palette.common.white, 0.14),
        padding: '0 4px',
        borderRadius: 4,
      },
      '& pre code': {
        backgroundColor: 'transparent',
        padding: 0,
      },
      '& a': {
        color: blue[600],
        textDecoration: 'none',
      },
      '& a:hover': {
        textDecoration: 'underline',
      },
      '& blockquote': {
        margin: '6px 0',
        padding: theme.spacing(0.25, 1),
        borderLeft: `3px solid ${alpha(blue[500], 0.5)}`,
        color: theme.palette.text.secondary,
      },
      '& h1, & h2, & h3, & h4, & h5, & h6': {
        margin: '8px 0 4px',
        fontWeight: 600,
        lineHeight: '20px',
      },
    },
    messageTimestamp: {
      marginTop: 2,
      fontSize: 11,
      opacity: 0.6,
    }
  });
});

const useComposerStyles = makeStyles((theme: Theme) =>
{
  const isLight = theme.palette.type === 'light';

  return ({
    newChatMessageBox: {
      border: 0,
      paddingLeft: theme.spacing(1),
      borderRadius: 20,
      backgroundColor: isLight ? alpha(theme.palette.common.black, 0.05) : alpha(theme.palette.common.white, 0.15),
      width: '100%',
    },
    iconButtonBase: {
      margin: -8,
      padding: 8,
    },
    attachFileIconButton: {
      marginLeft: -12,
      marginRight: 4,
    },
    attachFileButton: {
      transform: 'rotate(-135deg)',
    },
    sendMessageButton: {
      marginRight: -4,
    },
    messageInput: {
      padding: 8,
    }
  });
});

function AssistantMarkdown(props: { content: string })
{
  const classes = useMessageStyles();

  return (
    <ReactMarkdown
      className={classes.markdownContent}
      remarkPlugins={[remarkGfm]}
      components={{
        a: ({ node, color, ...linkProps }) => (
          <Link component="a" {...linkProps} target="_blank" rel="noopener noreferrer" />
        ),
      }}
    >
      {props.content}
    </ReactMarkdown>
  );
}

function MessageBubble(props: { message: ChatMessage })
{
  const classes = useMessageStyles();
  const isUser = props.message.role === 'user';

  return (
    <div className={`${classes.messageRow} ${isUser ? classes.messageRowUser : classes.messageRowAssistant}`}>
      <div className={`${classes.messageBubble} ${isUser ? classes.messageBubbleUser : classes.messageBubbleAssistant}`}>
        {isUser
          ? <Typography variant="body2">{props.message.content}</Typography>
          : <AssistantMarkdown content={props.message.content} />}
        <div className={classes.messageTimestamp}>{new Date(props.message.timestamp).toLocaleTimeString()}</div>
      </div>
    </div>
  );
}

function StreamingMessageBubble(props: { content: string })
{
  const classes = useMessageStyles();

  return (
    <div className={`${classes.messageRow} ${classes.messageRowAssistant}`}>
      <div className={`${classes.messageBubble} ${classes.messageBubbleAssistant}`}>
        <AssistantMarkdown content={props.content} />
      </div>
    </div>
  );
}

function MessageList(props: { messages: ChatMessage[]; streamingMessage: string | null; isThreadReady: boolean; loadingText: string })
{
  const classes = useMessageStyles();

  return (
    <div className={classes.messagesContainer}>
      {!props.isThreadReady && (
        <Typography variant="body2" color="textSecondary">
          {props.loadingText}
        </Typography>
      )}
      {props.messages.map(message => (
        <MessageBubble key={message.id} message={message} />
      ))}
      {props.streamingMessage && (
        <StreamingMessageBubble content={props.streamingMessage} />
      )}
    </div>
  );
}

type MessageComposerProps = {
  messageInput: ReturnType<typeof useInput>;
  sendingMessage: boolean;
  isThreadReady: boolean;
  onSend: () => void;
  onPdfUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  messageInputRef: React.RefObject<HTMLTextAreaElement | HTMLInputElement>;
  fileInputRef: React.RefObject<HTMLInputElement>;
  placeholder: string;
};

function MessageComposer(props: MessageComposerProps)
{
  const classes = useComposerStyles();

  return (
    <Paper className={classes.newChatMessageBox} elevation={0} variant="outlined">
      <input
        type="file"
        ref={props.fileInputRef}
        accept="application/pdf"
        style={{ display: 'none' }}
        onChange={props.onPdfUpload}
      />
      <InputBase
        {...props.messageInput.props}
        fullWidth
        multiline
        disabled={props.sendingMessage || !props.isThreadReady}
        placeholder={props.placeholder}
        className={classes.messageInput}
        inputProps={{ maxLength: 100 }}
        inputRef={props.messageInputRef}
        onKeyDown={e =>
        {
          if(e.key === 'Enter' && !e.shiftKey)
          {
            e.preventDefault();
            if(!props.sendingMessage && props.messageInput.props.value && props.isThreadReady) props.onSend();
          }
        }}
        startAdornment={
          <IconButton size="small" disabled={props.sendingMessage || !props.isThreadReady} onClick={() => props.fileInputRef.current?.click()} color="default" className={`${classes.iconButtonBase} ${classes.attachFileIconButton}`}>
            <AttachFileIcon fontSize="medium" className={classes.attachFileButton} />
          </IconButton>
        }
        endAdornment={
          <IconButton size="small" disabled={!props.messageInput.props.value || props.sendingMessage || !props.isThreadReady} onClick={props.onSend} color="primary" className={`${classes.iconButtonBase} ${classes.sendMessageButton}`} >
            {props.sendingMessage ? <CircularProgress size={24} /> : <SendChatMessageIcon fontSize="medium" />}
          </IconButton>
        }
      />
    </Paper>
  );
}

export default function Assistant(props: {
  open: boolean;
  onClose: () => void;
})
{
  const { t } = useTranslation();
  const classes = useAssistantStyles();

  const messageInput = useInput();
  const [sendingMessage, setSendingMessage] = useState(false);
  const messageInputRef = useRef<HTMLTextAreaElement | HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { threadId, isThreadReady, messages, streamingMessage, addMessage } = useAssistantRealtime({
    open: props.open,
    errorFallback: t('partnerPortal.assistant.error')
  });
  const [sendAssistantMessageMutation] = useSendAssistantMessageMutation();

  const handlePdfUpload = useCallback((event: React.ChangeEvent<HTMLInputElement>) =>
  {
    const file = event.target.files?.[0];
    if(file && file.type === 'application/pdf')
    {
      // TODO: Implement PDF upload logic
      console.log('Uploading PDF:', file.name);
    }
    // Reset input value to allow selecting the same file again
    if(fileInputRef.current)
    {
      fileInputRef.current.value = '';
    }
  }, []);

  const sendMessage = useCallback(async () =>
  {
    if(!messageInput.props.value || !threadId) return;

    setSendingMessage(true);
    try
    {
      const userMessage = messageInput.props.value.toString();
      const timestamp = new Date().toISOString();

      addMessage({ id: `user-${Date.now()}`, role: 'user', content: userMessage, timestamp });

      await sendAssistantMessageMutation({
        variables: {
          input: {
            ThreadId: threadId,
            Message: userMessage,
            FileIds: []
          }
        }
      });

      messageInput.setValue("");
    } catch(error)
    {
      console.error('Error sending message:', error);
    } finally
    {
      setSendingMessage(false);
      // Refocus after re-enabling the input
      setTimeout(() => messageInputRef.current?.focus(), 0);
    }
  }, [addMessage, messageInput, messageInputRef, sendAssistantMessageMutation, threadId]);

  return <Dialog open={props.open} onClose={props.onClose} fullWidth maxWidth="sm" scroll="paper">
    <DialogTitle className={classes.dialogTitle}><Typography variant="h6"> {t('partnerPortal.assistant.title')}</Typography><IconButton className={classes.closeButton} onClick={props.onClose}><CloseIcon /></IconButton></DialogTitle>
    <DialogContent dividers className={classes.dialogContent}>
      <DialogContentText>
        <AssistantIcon className={classes.chatMessageIcon} /> {t('partnerPortal.assistant.welcomeMessage')}
      </DialogContentText>
      <MessageList
        messages={messages}
        streamingMessage={streamingMessage}
        isThreadReady={isThreadReady}
        loadingText={t('partnerPortal.assistant.creatingThread', 'Kapcsolódás...')}
      />
    </DialogContent>
    <DialogActions className={classes.dialogActions}>
      <MessageComposer
        messageInput={messageInput}
        sendingMessage={sendingMessage}
        isThreadReady={isThreadReady}
        onSend={sendMessage}
        onPdfUpload={handlePdfUpload}
        messageInputRef={messageInputRef}
        fileInputRef={fileInputRef}
        placeholder={t('partnerPortal.assistant.newMessagePlaceholder')}
      />
    </DialogActions>
  </Dialog>
}
